import { type ReactNode, createContext, useCallback, useMemo, useState } from 'react';

import enCatalog from './locales/en.json';
import ptBrCatalog from './locales/pt-BR.json';

export type Locale = 'pt-BR' | 'en';

export type TranslationCatalog = Record<string, string>;

export interface I18nContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const CATALOGS: Record<Locale, TranslationCatalog> = {
  'pt-BR': ptBrCatalog as TranslationCatalog,
  en: enCatalog as TranslationCatalog,
};

export const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

export interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale = 'pt-BR' }: I18nProviderProps) {
  const [locale, setLocale] = useState<Locale>(initialLocale);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const primary = CATALOGS[locale][key];
      if (primary !== undefined) return interpolate(primary, vars);
      const fallback = CATALOGS['pt-BR'][key];
      if (fallback !== undefined) return interpolate(fallback, vars);
      return key;
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
